import {
  BYO_IMAGE_MIME_TYPES,
  BYO_MAX_CAROUSEL_ITEMS,
  BYO_MAX_IMAGE_UPLOAD_BYTES,
  BYO_MAX_VIDEO_UPLOAD_BYTES,
  BYO_VIDEO_MIME_TYPES,
} from '@mondaygpt/types'
import type { IntentShape } from '../components/ShapeSlider'

/**
 * Bring-your-own-content media rules, shared by both doors (Home hero ready
 * mode + calendar day-tap picker). Mirrors the server's classifyMedia in
 * byo-post.service.ts so the user gets an inline nudge instead of a 400.
 */

// Widened to string[] because File.type is a plain string and `.includes`
// narrows on tuple literals.
export const BYO_VIDEO_TYPES: readonly string[] = BYO_VIDEO_MIME_TYPES
export const BYO_IMAGE_TYPES: readonly string[] = BYO_IMAGE_MIME_TYPES
export const BYO_ACCEPT_ATTR = [...BYO_VIDEO_MIME_TYPES, ...BYO_IMAGE_MIME_TYPES].join(',')
export const BYO_MAX_VIDEO_MB = Math.round(BYO_MAX_VIDEO_UPLOAD_BYTES / (1024 * 1024))
export const BYO_MAX_IMAGE_MB = Math.round(BYO_MAX_IMAGE_UPLOAD_BYTES / (1024 * 1024))

// Generation INGREDIENT caps (the hero shelf + the popover's create room) —
// tighter than the BYO as-is caps above because ingredients feed the engine,
// they don't publish as-is.
export const INGREDIENT_MAX_IMAGE_BYTES = 15 * 1024 * 1024
export const INGREDIENT_MAX_VIDEO_BYTES = 50 * 1024 * 1024

export type ByoMediaKind = 'video' | 'image' | 'carousel'

export function byoMediaKind(mimeTypes: string[]): ByoMediaKind {
  const videos = mimeTypes.filter(t => BYO_VIDEO_TYPES.includes(t)).length
  if (videos > 0) return 'video'
  return mimeTypes.length >= 2 ? 'carousel' : 'image'
}

/** Mix rules only — for media that already made it through an upload. */
export function byoMixError(mimeTypes: string[]): string | null {
  const videos = mimeTypes.filter(t => BYO_VIDEO_TYPES.includes(t)).length
  const images = mimeTypes.filter(t => BYO_IMAGE_TYPES.includes(t)).length
  if (videos + images !== mimeTypes.length) {
    return "That file type isn't supported. Use MP4, MOV, JPEG, PNG, or WebP."
  }
  if (videos > 0 && images > 0) return 'One post can carry a video or images, not both.'
  if (videos > 1) return 'Just one video per post.'
  if (images > BYO_MAX_CAROUSEL_ITEMS) return `Up to ${BYO_MAX_CAROUSEL_ITEMS} images per post.`
  return null
}

/**
 * The Post-your-own dropzone invite, retold by the declared format (null =
 * nothing declared yet, the generic invite). Shared by the Home hero's own
 * room and the calendar popover's mini form so the two doors never drift.
 */
export function byoInviteFor(choice: IntentShape | null): {
  title: string
  sub: string
  frames: number
} {
  switch (choice) {
    case 'short_vertical_video':
      return {
        title: 'Drop your finished video here',
        sub: 'One video, MP4 or MOV. Taka publishes it exactly as uploaded. No changes.',
        frames: 1,
      }
    case 'carousel':
      return {
        title: 'Drop your carousel photos here',
        sub: '2 to 10 photos, posted in your order. Taka publishes them exactly as uploaded. No changes.',
        frames: 3,
      }
    case 'static_image':
      return {
        title: 'Drop your finished photo here',
        sub: 'One photo. Taka publishes it exactly as uploaded. No changes.',
        frames: 1,
      }
    default:
      return {
        title: 'Drop your finished video or photos here',
        sub: 'One video, or up to 10 photos. Taka publishes them exactly as uploaded. No changes.',
        frames: 3,
      }
  }
}

/** The declared format narrows the file picker to the mimes it can become. */
export function byoAcceptAttrFor(choice: IntentShape | null): string {
  if (choice === 'short_vertical_video') return BYO_VIDEO_TYPES.join(',')
  if (choice) return BYO_IMAGE_TYPES.join(',')
  return BYO_ACCEPT_ATTR
}

/** Mix + per-file size rules — for freshly picked Files, before upload. */
export function byoFilesError(files: File[]): string | null {
  const mixError = byoMixError(files.map(f => f.type))
  if (mixError) return mixError
  for (const file of files) {
    if (BYO_VIDEO_TYPES.includes(file.type) && file.size > BYO_MAX_VIDEO_UPLOAD_BYTES) {
      return `${file.name} is over ${BYO_MAX_VIDEO_MB}MB.`
    }
    if (BYO_IMAGE_TYPES.includes(file.type) && file.size > BYO_MAX_IMAGE_UPLOAD_BYTES) {
      return `${file.name} is over ${BYO_MAX_IMAGE_MB}MB.`
    }
  }
  return null
}
